// useNodeData.js
// Hook for reading the current node and its child nodes (subscription + cached snapshot).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getChildNodesForUser,
  subscribeToChildNodes,
  subscribeToNode,
} from 'services/nodes/NodeReadService';

import { timestampToMillis } from 'utilities/DateTime';

const useSubscribedChildNodes = (nodeKey, user, parentNode, baseKey, enabled = true) => {
  const [children, setChildren] = useState([]);
  const [hasEmitted, setHasEmitted] = useState(false);
  const parentNodeRef = useRef(parentNode);

  parentNodeRef.current = parentNode;

  useEffect(() => {
    setChildren([]);
    setHasEmitted(false);

    if (!enabled || !nodeKey || !user || !baseKey) {
      setHasEmitted(true);
      return undefined;
    }

    const unsubscribe = subscribeToChildNodes(
      nodeKey,
      user,
      parentNodeRef.current,
      (nextChildren) => {
        setChildren(Array.isArray(nextChildren) ? nextChildren : []);
        setHasEmitted(true);
      },
    );
    return () => unsubscribe?.();
  }, [enabled, nodeKey, user, baseKey]);

  return {
    children,
    hasEmitted,
  };
};

/**
 * Subscribes to the current node and its children.
 *
 * Single source of truth:
 * - `currentNode` lives in NavigationContext.
 * - Firestore subscription updates `currentNode` when newer.
 * - Child nodes use subscription + cached snapshot.
 *
 * @param {object} params
 * @param {object|null} params.currentNode - The current node object.
 * @param {function} params.setCurrentNode - Function to update the current node.
 * @param {object} params.user - The current user object.
 * @returns {{ node: any, nodeKey: string | null, childNodes: any[], areChildNodesReady: boolean }}
 */
export default function useNodeData({ currentNode, setCurrentNode, user }) {
  const queryClient = useQueryClient();

  const nodeKey = currentNode?.key ?? null;
  const parentBaseKey = typeof currentNode?.baseKey === 'string'
    ? currentNode.baseKey.trim()
    : '';

  const parentNodeForPermissions = useMemo(() => {
    if (!currentNode?.key || !parentBaseKey) return null;

    return {
      ...currentNode,
      baseKey: parentBaseKey,
    };
  }, [currentNode, parentBaseKey]);

  useEffect(() => {
    if (!nodeKey) return undefined;
    if (typeof setCurrentNode !== 'function') return undefined;

    const unsubscribe = subscribeToNode(nodeKey, (next) => {
      if (!next) {
        setCurrentNode((prev) => (prev?.key === nodeKey ? null : prev));
        return;
      }

      setCurrentNode((prev) => {
        if (!prev || prev.key !== nodeKey) {
          return next;
        }

        const nextModified = timestampToMillis(next.dateModified) || timestampToMillis(next.dateCreated);
        const currModified = timestampToMillis(prev.dateModified) || timestampToMillis(prev.dateCreated);

        if (nextModified > currModified) {
          return next;
        }

        return prev;
      });
    });

    return () => unsubscribe?.();
  }, [nodeKey, setCurrentNode]);

  // TanStack Query: fetch a one-time snapshot so initial renders come from cache.
  const childNodesQueryKey = useMemo(
    () => ['node', nodeKey ?? null, 'children', user?.key ?? null],
    [nodeKey, user?.key]
  );

  const memberChildrenEnabled = Boolean(
    nodeKey && user && parentNodeForPermissions?.baseKey,
  );

  const childNodesQuery = useQuery({
    queryKey: childNodesQueryKey,
    queryFn: () => getChildNodesForUser(nodeKey, user, parentNodeForPermissions),
    enabled: memberChildrenEnabled,
    staleTime: 30_000,
    gcTime: 300_000,
  });

  const {
    children: subscribedChildNodes,
    hasEmitted: hasSubscriptionEmitted,
  } = useSubscribedChildNodes(
    nodeKey,
    user,
    parentNodeForPermissions,
    parentBaseKey,
    memberChildrenEnabled,
  );

  useEffect(() => {
    if (!memberChildrenEnabled) return;
    if (!nodeKey) return;
    if (!Array.isArray(subscribedChildNodes)) return;
    queryClient.setQueryData(childNodesQueryKey, subscribedChildNodes);
  }, [memberChildrenEnabled, nodeKey, subscribedChildNodes, queryClient, childNodesQueryKey]);

  const childNodes = useMemo(() => {
    if (Array.isArray(subscribedChildNodes) && subscribedChildNodes.length > 0) {
      return subscribedChildNodes;
    }
    if (Array.isArray(childNodesQuery.data)) {
      return childNodesQuery.data;
    }
    return Array.isArray(subscribedChildNodes) ? subscribedChildNodes : [];
  }, [childNodesQuery.data, subscribedChildNodes]);

  const areChildNodesReady = (
    !memberChildrenEnabled
    || hasSubscriptionEmitted
    || (childNodesQuery.isFetched && Array.isArray(childNodesQuery.data) && childNodesQuery.data.length > 0)
  );

  return {
    node: currentNode,
    nodeKey,
    childNodes,
    areChildNodesReady,
    parentNodes: [],
  };
}
